import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header, Footer, MobileBottomNav } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';
import { rpsApi, walletApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Trophy, Users, Clock, Sparkles, DollarSign, 
  Play, Shield, Video, Hash
} from 'lucide-react';

const RPS_MOVES = [
  { id: 'rock', emoji: '✊', name: 'Rock' },
  { id: 'paper', emoji: '🖐️', name: 'Paper' },
  { id: 'scissors', emoji: '✌️', name: 'Scissors' }
];

const GAME_MODES = [
  { id: 'commit_reveal', name: 'Commit-Reveal', icon: Hash, desc: 'Cheat-proof digital mode', recommended: true },
  { id: 'board_reveal', name: 'Board Reveal', icon: Shield, desc: 'Write and reveal together' },
  { id: 'webcam', name: 'Live Webcam', icon: Video, desc: 'Show hands on camera' }
];

const RPSPage = () => {
  const { user } = useAuth();
  const { addListener } = useWebSocket() || {};
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [myChallenges, setMyChallenges] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [activeGame, setActiveGame] = useState(null);
  
  // Create challenge form
  const [createForm, setCreateForm] = useState({
    stake_amount: 10,
    stake_type: 'qbean',
    mode: 'commit_reveal',
    with_moderator: false
  });

  // Game state
  const [selectedMove, setSelectedMove] = useState(null);
  const [commitHash, setCommitHash] = useState('');
  const [salt, setSalt] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  // Real-time updates via WebSocket
  useEffect(() => {
    if (!addListener) return;
    return addListener((event) => {
      if (event.type?.startsWith('rps_')) {
        fetchData();
      }
    });
  }, [addListener]);

  const fetchData = async () => {
    try {
      const [walletRes, challengesRes, myRes, leaderRes] = await Promise.all([
        walletApi.getBalance(),
        rpsApi.getChallenges(),
        rpsApi.getMyChallenges(),
        rpsApi.getLeaderboard()
      ]);
      setWallet(walletRes.data);
      setChallenges(challengesRes.data);
      setMyChallenges(myRes.data);
      setLeaderboard(leaderRes.data);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateChallenge = async () => {
    try {
      const { data } = await rpsApi.create(createForm);
      toast.success('Challenge created!');
      setShowCreate(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create challenge');
    }
  };

  const handleAcceptChallenge = async (challengeId) => {
    try {
      await rpsApi.accept(challengeId);
      toast.success('Challenge accepted! Entering duel arena...');
      navigate(`/rps/duel/${challengeId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept challenge');
    }
  };

  const generateSalt = () => {
    return Array.from(crypto.getRandomValues(new Uint8Array(16)))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  };

  const hashMove = async (move, salt) => {
    const text = `${move}:${salt}`;
    const msgBuffer = new TextEncoder().encode(text);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const handleCommitMove = async () => {
    if (!selectedMove || !activeGame) return;
    
    const newSalt = generateSalt();
    const hash = await hashMove(selectedMove, newSalt);
    setSalt(newSalt);
    setCommitHash(hash);

    try {
      await rpsApi.commitMove({
        challenge_id: activeGame.id,
        move_hash: hash
      });
      toast.success('Move committed! Waiting for opponent...');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to commit move');
    }
  };

  const handleRevealMove = async () => {
    if (!selectedMove || !salt || !activeGame) return;

    try {
      const { data } = await rpsApi.revealMove({
        challenge_id: activeGame.id,
        move: selectedMove,
        salt: salt
      });
      
      if (data.round_winner) {
        toast.success(`Round result: ${data.round_winner === 'tie' ? 'Tie!' : `${data.round_winner} wins!`}`);
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reveal move');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A1A1A]">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1A1A1A] flex flex-col pb-20 md:pb-0">
      <Header />
      
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero */}
        <div className="card-dark bg-[#0F0F0F] text-white mb-8" data-testid="rps-hero">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 text-5xl mb-4">
                <span className="rps-hand">✊</span>
                <span className="rps-hand">✌️</span>
                <span className="rps-hand">🖐️</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-black uppercase" style={{ fontFamily: 'Outfit' }}>
                Rock Paper Scissors <span className="text-[#28A745]">Live</span>
              </h1>
              <p className="text-gray-400 mt-2">Play real humans. Stake real money. Win instantly.</p>
            </div>
            <Button 
              onClick={() => setShowCreate(true)} 
              className="btn-primary text-lg"
              data-testid="create-challenge-btn"
            >
              <Play className="h-5 w-5 mr-2" /> Create Challenge
            </Button>
          </div>
        </div>

        {/* Create Challenge Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="card-dark w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold mb-6 text-white" style={{ fontFamily: 'Outfit' }}>Create RPS Challenge</h2>
              
              <div className="space-y-5">
                {/* Game Mode */}
                <div>
                  <Label className="font-semibold text-white">Game Mode</Label>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {GAME_MODES.map((mode) => {
                      const Icon = mode.icon;
                      return (
                        <button
                          key={mode.id}
                          onClick={() => setCreateForm({ ...createForm, mode: mode.id })}
                          className={`p-3 rounded-lg border-2 border-black text-center ${
                            createForm.mode === mode.id ? 'bg-[#D4AF37] text-[#1A1A1A]' : 'bg-[#2D2D2D] text-white border-[#3D3D3D]'
                          }`}
                          data-testid={`mode-${mode.id}`}
                        >
                          <Icon className="h-6 w-6 mx-auto mb-1" />
                          <div className="text-xs font-semibold">{mode.name}</div>
                          {mode.recommended && (
                            <span className="text-[10px] bg-[#22C55E] text-white px-1 rounded">Best</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Stake Type */}
                <div>
                  <Label className="font-semibold text-white">Stake Currency</Label>
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => setCreateForm({ ...createForm, stake_type: 'qbean' })}
                      className={`flex-1 p-3 rounded-lg border-2 font-semibold ${
                        createForm.stake_type === 'qbean' ? 'bg-[#D4AF37] text-[#1A1A1A] border-[#D4AF37]' : 'bg-[#2D2D2D] text-white border-[#3D3D3D]'
                      }`}
                    >
                      <Sparkles className="h-5 w-5 inline mr-2" /> Qbean
                    </button>
                    <button
                      onClick={() => setCreateForm({ ...createForm, stake_type: 'real' })}
                      className={`flex-1 p-3 rounded-lg border-2 font-semibold ${
                        createForm.stake_type === 'real' ? 'bg-[#28A745] text-white border-[#28A745]' : 'bg-[#2D2D2D] text-white border-[#3D3D3D]'
                      }`}
                    >
                      <DollarSign className="h-5 w-5 inline mr-2" /> Real Money
                    </button>
                  </div>
                </div>

                {/* Stake Amount */}
                <div>
                  <Label className="font-semibold text-white">Stake Amount</Label>
                  <Input
                    type="number"
                    min="1"
                    max="10000"
                    value={createForm.stake_amount}
                    onChange={(e) => setCreateForm({ ...createForm, stake_amount: parseFloat(e.target.value) || 0 })}
                    className="input-dark mt-1"
                    data-testid="stake-input"
                  />
                  <div className="flex gap-2 mt-2">
                    {[10, 50, 100, 500].map((amt) => (
                      <button
                        key={amt}
                        onClick={() => setCreateForm({ ...createForm, stake_amount: amt })}
                        className={`flex-1 py-1 rounded border-2 text-sm font-semibold ${
                          createForm.stake_amount === amt ? 'bg-[#D4AF37] text-[#1A1A1A] border-[#D4AF37]' : 'bg-[#2D2D2D] text-white border-[#3D3D3D]'
                        }`}
                      >
                        {amt}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Moderator */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="moderator"
                    checked={createForm.with_moderator}
                    onChange={(e) => setCreateForm({ ...createForm, with_moderator: e.target.checked })}
                    className="h-5 w-5 border-2 border-black rounded"
                  />
                  <Label htmlFor="moderator" className="cursor-pointer text-white">
                    Request moderator (for high-stakes games)
                  </Label>
                </div>

                <div className="flex gap-3">
                  <Button onClick={() => setShowCreate(false)} className="btn-secondary flex-1">
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleCreateChallenge} 
                    className="btn-primary flex-1"
                    data-testid="confirm-create-btn"
                  >
                    Create Challenge
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Open Challenges */}
          <div className="lg:col-span-2">
            <h2 className="text-xl font-bold uppercase mb-4 text-white" style={{ fontFamily: 'Outfit' }}>
              <Users className="h-5 w-5 inline mr-2 text-[#D4AF37]" />
              Open Challenges
            </h2>
            
            {challenges.length === 0 ? (
              <div className="card-dark text-center py-12">
                <span className="text-4xl mb-4 block">✊✌️🖐️</span>
                <p className="text-gray-400 mb-4">No open challenges</p>
                <Button onClick={() => setShowCreate(true)} className="btn-primary">
                  Be the First!
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {challenges.map((challenge) => (
                  <div key={challenge.id} className="card-dark card-dark-interactive" data-testid={`challenge-${challenge.id}`}>
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="font-bold text-white">{challenge.creator_name}</div>
                        <div className="text-xs text-gray-400">
                          {GAME_MODES.find(m => m.id === challenge.mode)?.name || challenge.mode}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-[#D4AF37]">
                          {challenge.stake_type === 'real' ? '$' : ''}{challenge.stake_amount}{challenge.stake_type === 'qbean' ? ' QBC' : ''}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between text-xs text-gray-400 mb-4">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Expires {new Date(challenge.expires_at).toLocaleDateString()}
                      </span>
                      {challenge.with_moderator && (
                        <span className="flex items-center gap-1 text-[#6366F1]">
                          <Shield className="h-3 w-3" /> Moderated
                        </span>
                      )}
                    </div>

                    {challenge.creator_id !== user?.id && (
                      <Button 
                        onClick={() => handleAcceptChallenge(challenge.id)}
                        className="btn-primary w-full text-sm"
                        data-testid={`accept-${challenge.id}`}
                      >
                        Accept Challenge
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* My Active Games */}
            {myChallenges.filter(c => c.status === 'in_progress').length > 0 && (
              <div className="mt-8">
                <h2 className="text-xl font-bold uppercase mb-4 text-white" style={{ fontFamily: 'Outfit' }}>
                  Active Games
                </h2>
                {myChallenges.filter(c => c.status === 'in_progress').map((game) => (
                  <div key={game.id} className="card-dark bg-gradient-to-r from-[#28A745]/20 to-[#28A745]/5 border border-[#28A745] text-white mb-4">
                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <div className="font-bold">
                          {game.creator_name} vs {game.opponent_name}
                        </div>
                        <div className="text-sm opacity-80">Round {game.current_round} of 3</div>
                      </div>
                      <div className="text-2xl font-black">
                        {game.creator_score} - {game.opponent_score}
                      </div>
                    </div>

                    {game.mode === 'commit_reveal' && (
                      <div className="bg-white/10 rounded-lg p-4">
                        <div className="flex justify-center gap-4 mb-4">
                          {RPS_MOVES.map((move) => (
                            <button
                              key={move.id}
                              onClick={() => setSelectedMove(move.id)}
                              className={`text-4xl p-2 rounded-lg transition-all ${
                                selectedMove === move.id ? 'bg-[#D4AF37] text-[#1A1A1A] scale-110' : 'hover:bg-white/20'
                              }`}
                              data-testid={`move-${move.id}`}
                            >
                              {move.emoji}
                            </button>
                          ))}
                        </div>
                        
                        <div className="flex gap-2">
                          {!commitHash ? (
                            <Button 
                              onClick={() => { setActiveGame(game); handleCommitMove(); }}
                              className="btn-secondary flex-1"
                              disabled={!selectedMove}
                            >
                              Lock In Move
                            </Button>
                          ) : (
                            <Button 
                              onClick={() => { setActiveGame(game); handleRevealMove(); }}
                              className="btn-secondary flex-1"
                            >
                              Reveal Move
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Wallet Quick View */}
            <div className="card-dark">
              <h3 className="font-bold mb-3 text-white">Your Balance</h3>
              <div className="flex gap-4">
                <div>
                  <div className="text-xs text-gray-400">Real</div>
                  <div className="font-bold text-[#28A745]">${wallet?.real_balance?.toFixed(2) || '0.00'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400">Qbean</div>
                  <div className="font-bold text-[#D4AF37]">{wallet?.qbean_balance?.toFixed(0) || '0'} QBC</div>
                </div>
              </div>
            </div>

            {/* Leaderboard */}
            <div className="card-dark">
              <h3 className="font-bold mb-4 flex items-center gap-2 text-white">
                <Trophy className="h-5 w-5 text-[#D4AF37]" />
                Hall of Fame
              </h3>
              
              {leaderboard.length === 0 ? (
                <p className="text-center text-gray-400 py-4">No champions yet</p>
              ) : (
                <div className="space-y-3">
                  {leaderboard.map((player, idx) => (
                    <div key={player.id} className="flex items-center justify-between" data-testid={`leader-${idx}`}>
                      <div className="flex items-center gap-3">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          idx === 0 ? 'bg-[#D4AF37] text-[#1A1A1A]' :
                          idx === 1 ? 'bg-gray-400 text-gray-800' :
                          idx === 2 ? 'bg-amber-700 text-white' :
                          'bg-[#2D2D2D] text-gray-400'
                        }`}>
                          {idx + 1}
                        </span>
                        <span className="font-semibold text-sm text-white">{player.full_name}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-[#28A745] text-sm">{player.wins}W</div>
                        <div className="text-xs text-gray-400">
                          {Math.round(player.win_rate * 100)}%
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Rules */}
            <div className="card-dark">
              <h3 className="font-bold mb-3 text-white">Quick Rules</h3>
              <ul className="text-sm space-y-2 text-gray-400">
                <li>• Best of 3 rounds wins</li>
                <li>• 10% platform fee on winnings</li>
                <li>• Commit-reveal prevents cheating</li>
                <li>• Disconnection = forfeit round</li>
              </ul>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <MobileBottomNav />
    </div>
  );
};

export default RPSPage;
